import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { groupBy } from 'lodash-es';

import { JobTitle } from '@pflegia/data-access';
import useJobTitle from '@pflegia/ui/shared/job-title/hooks/use-job-title';
import { getCityFallback } from '@pflegia/util/functions/job-position/helpers';

import { UseJobListFilterState } from '@shared/application/interfaces';

import useJobTitlesFilterList from '../application/use-job-titles-filter-list.hook';
import { JobListingFaqVariant } from '../constants/faq';
import JobBoardPageProps from '../types/job-board-page';
import { isPflegefachkraftRoleGroupActive } from '../utils/pflegefachkraft-role-group';

type UseFaqProps = {
  filterState: UseJobListFilterState;
  jobTitle?: JobBoardPageProps['jobTitle'];
  location?: JobBoardPageProps['location'];
  nearbyCities?: JobBoardPageProps['nearbyCities'];
  pflegefachkraftRoleGroup?: JobBoardPageProps['pflegefachkraftRoleGroup'];
};

const getFaqVariant = (jobPositionFilterValue: string, locationFilterValue: string): JobListingFaqVariant => {
  const hasPosition = Boolean(jobPositionFilterValue);
  const hasLocation = Boolean(locationFilterValue);

  if (hasPosition && hasLocation) return 'jobAndLocation';
  if (hasPosition) return 'positionOnly';
  if (hasLocation) return 'locationOnly';

  return 'none';
};

export const useFaq = ({ filterState, jobTitle, location, nearbyCities, pflegefachkraftRoleGroup }: UseFaqProps) => {
  const { translateJobTitle } = useJobTitle();
  const { jobTitles } = useJobTitlesFilterList();
  const groupedFilters = groupBy(filterState.filters, 'type');
  const locationFilters = groupedFilters?.Location || [];
  const positionFilters = groupedFilters?.Position || [];
  const hasMultipleLocations = locationFilters.length > 1;
  const isPflegefachkraftPosition = isPflegefachkraftRoleGroupActive(
    positionFilters.map(({ value }) => value),
    pflegefachkraftRoleGroup,
    !filterState.filtersFullyInitialized,
  );
  const hasMultiplePositions = positionFilters.length > 1 && !isPflegefachkraftPosition;
  const shouldUseRouteLocationFallback = !filterState.filtersFullyInitialized;

  const activeLocation = locationFilters[0]?.locationObject || (shouldUseRouteLocationFallback ? location : undefined);
  const locationFilterValue = hasMultipleLocations ? '' : getCityFallback(activeLocation) || '';
  const city = activeLocation?.city || '';

  const filteredPositionId = positionFilters[0]?.value || '';
  const routePositionId = jobTitle?.id || '';
  const shouldUseRoutePositionFallback =
    Boolean(routePositionId) && (!filterState.filtersFullyInitialized || filteredPositionId === routePositionId);
  const positionId = filteredPositionId || (shouldUseRoutePositionFallback ? routePositionId : '');

  const filteredJobTitle = jobTitles?.find(jt => jt.value === filteredPositionId)?.text || '';
  const routeJobTitle = jobTitle ? translateJobTitle(jobTitle) : '';
  const fallbackJobTitle = shouldUseRoutePositionFallback ? routeJobTitle || jobTitles?.find(jt => jt.value === routePositionId)?.text : '';
  const pflegefachkraftLabel = isPflegefachkraftPosition ? pflegefachkraftRoleGroup?.label : undefined;
  const jobPositionFilterValue = pflegefachkraftLabel ?? (hasMultiplePositions ? '' : filteredJobTitle || fallbackJobTitle || '');
  const faqVariant = getFaqVariant(jobPositionFilterValue, locationFilterValue);
  const shouldShowFaq = faqVariant !== 'none';
  const shouldLoadRelatedInfo = faqVariant === 'jobAndLocation' && Boolean(positionId) && Boolean(city);

  const { data: relatedInfoData } = useQuery<{ getRelatedJobTitles: JobTitle[]; getRelatedLocations: string[] }>(GET_RELATED_INFO, {
    variables: { id: positionId, limit: 3, city },
    skip: !shouldLoadRelatedInfo,
  });

  const relatedJobTitles = relatedInfoData?.getRelatedJobTitles.map(relatedJobTitle => ({
    title: translateJobTitle(relatedJobTitle),
    id: relatedJobTitle.id,
  }));
  const relatedLocations =
    (faqVariant === 'locationOnly' ? nearbyCities?.map(cityWithJobCount => cityWithJobCount.city) : relatedInfoData?.getRelatedLocations) ??
    [];

  return {
    shouldShowFaq,
    faqVariant,
    relatedJobTitles: relatedJobTitles || [],
    locationFilterValue,
    jobPositionFilterValue,
    hasMultipleFilters: hasMultipleLocations || hasMultiplePositions,
    relatedLocations,
  };
};

const GET_RELATED_INFO = gql`
  query GetRelatedInfo($id: UUID!, $limit: Float!, $city: String!) {
    getRelatedJobTitles(id: $id, limit: $limit) {
      id
      title
    }
    getRelatedLocations(id: $id, city: $city)
  }
`;
